"use client";

import { c as _c } from "react/compiler-runtime";
import { useT } from "@/lib/i18n";
import { useEffect, useMemo, useState } from "react";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
function cssVar(name: string): string {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
function withAlpha(color: string, alpha: number): string {
  const m = color.match(/^#([0-9a-f]{6})$/i);
  if (!m) return color;
  const n = parseInt(m[1], 16);
  return `rgba(${n >> 16 & 255},${n >> 8 & 255},${n & 255},${alpha})`;
}
interface Theme {
  gold: string;
  goldDim: string;
  text: string;
  grid: string;
  tipBg: string;
  tipBorder: string;
  tipText: string;
}
function readTheme(): Theme {
  const gold = cssVar("--accent-gold");
  return {
    gold,
    goldDim: withAlpha(gold, 0.45),
    text: cssVar("--text-muted"),
    grid: withAlpha(cssVar("--text-primary"), 0.06),
    tipBg: cssVar("--bg-card"),
    tipBorder: cssVar("--border-subtle"),
    tipText: cssVar("--text-primary")
  };
}
const SKILL = ["all", "a10", "wr30", "wr50", "wr75"] as const;
const PLAYERS = ["solo", "2p", "3p", "4p"] as const;
const LABEL: Record<string, string> = {
  all: "All",
  a10: "A10",
  wr30: ">30% WR",
  wr50: ">50% WR",
  wr75: ">75% WR",
  solo: "Solo",
  "2p": "2P",
  "3p": "3P",
  "4p": "4P"
};
interface CharacterStat {
  character: string;
  total: number;
  fatal: number;
  avg_damage: number;
  avg_turns: number;
}
interface Row {
  encounter_id: string;
  act: number;
  room_type: string;
  total: number;
  fatal: number;
  avg_damage: number;
  avg_turns: number;
  characters: CharacterStat[];
}
interface Series {
  brackets: Record<string, Row[]>;
  versions: Record<string, Row[]>;
  version_order: string[];
}
export interface MonsterEncounterRef {
  encounter_id: string;
  encounter_name: string;
}
function charColor(id: string, fallback: string): string {
  return cssVar(`--color-${id.toLowerCase()}`) || fallback;
}
function pick(rows: Row[] | undefined, id: string): Row | null {
  const hits = (rows || []).filter(r => r.encounter_id === id);
  if (!hits.length) return null;
  return hits.reduce((a, b) => b.total > a.total ? b : a);
}
function fatalPct(r: Row | CharacterStat | null): number | null {
  if (!r || !r.total) return null;
  return Math.round(r.fatal / r.total * 1000) / 10;
}
async function fetchSeries(ids: string): Promise<Series | null> {
  const res = await fetch(`${API}/api/runs/encounter-series?encounter=${ids}`);
  if (res.ok) return res.json();
  if (res.status !== 404) return null;
  const one = async (params: Record<string, string>) => {
    const q = new URLSearchParams({
      encounter: ids,
      limit: "200",
      ...params
    });
    const r = await fetch(`${API}/api/runs/encounter-stats?${q}`);
    const d = r.ok ? await r.json() : null;
    return (d?.encounters as Row[] || []).filter(x => ids.split(",").includes(x.encounter_id));
  };
  const vr = await fetch(`${API}/api/runs/versions`).then(r => r.ok ? r.json() : null).catch(() => null);
  const versions: string[] = vr?.stat_versions || [];
  const brackets: Record<string, Row[]> = {};
  for (const k of [...SKILL, ...PLAYERS]) brackets[k] = await one(k === "all" ? {} : {
    bracket: k
  });
  const byVersion: Record<string, Row[]> = {};
  for (const v of versions) byVersion[v] = await one({
    build_id: v
  });
  return {
    brackets,
    versions: byVersion,
    version_order: versions
  };
}
const axisOpts = (th: Theme, suffix: string, max?: number) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: th.tipBg,
      borderColor: th.tipBorder,
      borderWidth: 1,
      cornerRadius: 6,
      padding: 8,
      titleColor: th.tipText,
      bodyColor: th.tipText,
      callbacks: {
        label: (c: {
          parsed: {
            y: number | null;
          };
        }) => ` ${c.parsed.y ?? 0}${suffix}`
      }
    }
  },
  scales: {
    x: {
      ticks: {
        color: th.text,
        font: {
          size: 11
        }
      },
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      max,
      ticks: {
        color: th.text,
        font: {
          size: 11
        },
        callback: (v: number | string) => `${v}${suffix}`
      },
      grid: {
        color: th.grid
      }
    }
  }
});
export default function MonsterEncounterStats(t0) {
  const $ = _c(119);
  const {
    encounters
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== encounters) {
    t1 = encounters.map(_temp).join(",");
    $[0] = encounters;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ids = t1;
  const [series, setSeries] = useState(undefined);
  const [enc, setEnc] = useState("");
  const [bracket, setBracket] = useState("all");
  const [version, setVersion] = useState("");
  const [th, setTh] = useState(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setTh(readTheme());
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== ids) {
    t4 = () => {
      if (!ids) {
        return;
      }
      fetchSeries(ids).then(setSeries).catch(() => setSeries(null));
    };
    t5 = [ids];
    $[4] = ids;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== enc || $[8] !== encounters[0]?.encounter_id || $[9] !== series) {
    t6 = () => {
      if (!series || enc) {
        return;
      }
      const all = series.brackets.all || [];
      const best = all.reduce(_temp2, null);
      setEnc(best?.encounter_id || encounters[0]?.encounter_id || "");
    };
    $[7] = enc;
    $[8] = encounters[0]?.encounter_id;
    $[9] = series;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== enc || $[12] !== encounters || $[13] !== series) {
    t7 = [series, enc, encounters];
    $[11] = enc;
    $[12] = encounters;
    $[13] = series;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== encounters || $[16] !== series?.brackets) {
    let t9;
    if ($[18] !== series?.brackets) {
      t9 = e_1 => pick(series?.brackets.all, e_1.encounter_id);
      $[18] = series?.brackets;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = encounters.filter(t9);
    $[15] = encounters;
    $[16] = series?.brackets;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const withData = t8;
  if (!ids || series === null) {
    return null;
  }
  if (series === undefined || !th) {
    let t9;
    if ($[20] !== t) {
      t9 = t("Loading");
      $[20] = t;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== t9) {
      t10 = <div className="text-sm text-[var(--text-muted)] py-4">{t9}…</div>;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  if (!withData.length) {
    return null;
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t9;
  if ($[24] !== bracket || $[25] !== enc || $[26] !== series.brackets || $[27] !== series.version_order || $[28] !== series.versions || $[29] !== t || $[30] !== th || $[31] !== version || $[32] !== withData) {
    const current = version ? pick(series.versions[version], enc) : pick(series.brackets[bracket], enc);
    let t19;
    if ($[43] !== enc || $[44] !== series.brackets) {
      t19 = k => fatalPct(pick(series.brackets[k], enc));
      $[43] = enc;
      $[44] = series.brackets;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    const skillData = SKILL.map(t19);
    let t20;
    if ($[46] !== enc || $[47] !== series.brackets) {
      t20 = k_0 => fatalPct(pick(series.brackets[k_0], enc));
      $[46] = enc;
      $[47] = series.brackets;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    const playerData = PLAYERS.map(t20);
    const versionOrder = [...series.version_order].reverse();
    let t21;
    if ($[49] !== enc || $[50] !== series.versions) {
      t21 = v => fatalPct(pick(series.versions[v], enc));
      $[49] = enc;
      $[50] = series.versions;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    const versionData = versionOrder.map(t21);
    const chars = (current?.characters || []).filter(_temp4);
    const charLabels = chars.map(_temp5);
    const yMax = Math.max(1, ...[...skillData, ...playerData, ...versionData].filter(_temp6)) * 1.3;
    const tile = _temp7;
    const pill = _temp8;
    t15 = "mt-4 space-y-4";
    if ($[52] !== enc || $[53] !== withData) {
      t16 = withData.length > 1 && <div className="flex flex-wrap items-center gap-1.5">{withData.map(e_2 => <button key={e_2.encounter_id} type="button" onClick={() => setEnc(e_2.encounter_id)} className={pill(enc === e_2.encounter_id)}>{e_2.encounter_name}</button>)}</div>;
      $[52] = enc;
      $[53] = withData;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    let t22;
    if ($[55] !== t) {
      t22 = t("Bracket");
      $[55] = t;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== t22) {
      t23 = <span className="text-xs text-[var(--text-muted)] w-16">{t22}</span>;
      $[57] = t22;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    let t24;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = [...SKILL, ...PLAYERS];
      $[59] = t24;
    } else {
      t24 = $[59];
    }
    let t25;
    if ($[60] !== bracket || $[61] !== t || $[62] !== version) {
      t25 = t24.map(k_1 => <button key={k_1} type="button" onClick={() => {
        setBracket(k_1);
        setVersion("");
      }} className={pill(!version && bracket === k_1)}>{t(LABEL[k_1])}</button>);
      $[60] = bracket;
      $[61] = t;
      $[62] = version;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== t23 || $[65] !== t25) {
      t26 = <div className="flex flex-wrap items-center gap-1.5">{t23}{t25}</div>;
      $[64] = t23;
      $[65] = t25;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] !== series.version_order || $[68] !== t || $[69] !== version) {
      t27 = series.version_order.length > 0 && <div className="flex flex-wrap items-center gap-1.5"><span className="text-xs text-[var(--text-muted)] w-16">{t("Version")}</span><select value={version} onChange={e_3 => setVersion(e_3.target.value)} className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)] px-2 py-1 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent-gold)]" aria-label={t("Game version")}><option value="">{t("All versions")}</option>{series.version_order.map(_temp9)}</select></div>;
      $[67] = series.version_order;
      $[68] = t;
      $[69] = version;
      $[70] = t27;
    } else {
      t27 = $[70];
    }
    if ($[71] !== t26 || $[72] !== t27) {
      t17 = <div className="flex flex-col gap-2">{t26}{t27}</div>;
      $[71] = t26;
      $[72] = t27;
      $[73] = t17;
    } else {
      t17 = $[73];
    }
    t18 = current ? <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{tile(t("Runs"), current.total.toLocaleString())}{tile(t("Fatal"), `${fatalPct(current)}%`)}{tile(t("Avg damage"), current.avg_damage.toFixed(1))}{tile(t("Avg turns"), current.avg_turns.toFixed(1))}</div> : <div className="text-sm text-[var(--text-muted)]">{t("No community data for this selection yet.")}</div>;
    t9 = "grid grid-cols-1 md:grid-cols-2 gap-3";
    let t28;
    if ($[74] !== t) {
      t28 = t("Fatal rate by skill tier");
      $[74] = t;
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    let t29;
    if ($[76] !== t28) {
      t29 = <div className="text-xs text-[var(--text-muted)] mb-2">{t28}</div>;
      $[76] = t28;
      $[77] = t29;
    } else {
      t29 = $[77];
    }
    let t30;
    if ($[78] !== t) {
      t30 = k_2 => t(LABEL[k_2]);
      $[78] = t;
      $[79] = t30;
    } else {
      t30 = $[79];
    }
    let t31;
    if ($[80] !== bracket || $[81] !== th || $[82] !== version) {
      t31 = k_3 => !version && k_3 === bracket ? th.gold : th.goldDim;
      $[80] = bracket;
      $[81] = th;
      $[82] = version;
      $[83] = t31;
    } else {
      t31 = $[83];
    }
    let t32;
    if ($[84] !== th || $[85] !== yMax) {
      t32 = axisOpts(th, "%", yMax);
      $[84] = th;
      $[85] = yMax;
      $[86] = t32;
    } else {
      t32 = $[86];
    }
    const t33 = <div className="h-44"><Bar data={{
        labels: SKILL.map(t30),
        datasets: [{
          data: skillData,
          backgroundColor: SKILL.map(t31),
          borderRadius: 4
        }]
      }} options={t32} /></div>;
    if ($[87] !== t29 || $[88] !== t33) {
      t10 = <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3">{t29}{t33}</div>;
      $[87] = t29;
      $[88] = t33;
      $[89] = t10;
    } else {
      t10 = $[89];
    }
    let t34;
    if ($[90] !== t) {
      t34 = t("Fatal rate by party size");
      $[90] = t;
      $[91] = t34;
    } else {
      t34 = $[91];
    }
    let t35;
    if ($[92] !== t34) {
      t35 = <div className="text-xs text-[var(--text-muted)] mb-2">{t34}</div>;
      $[92] = t34;
      $[93] = t35;
    } else {
      t35 = $[93];
    }
    let t36;
    if ($[94] !== t) {
      t36 = k_4 => t(LABEL[k_4]);
      $[94] = t;
      $[95] = t36;
    } else {
      t36 = $[95];
    }
    let t37;
    if ($[96] !== bracket || $[97] !== th || $[98] !== version) {
      t37 = k_5 => !version && k_5 === bracket ? th.gold : th.goldDim;
      $[96] = bracket;
      $[97] = th;
      $[98] = version;
      $[99] = t37;
    } else {
      t37 = $[99];
    }
    let t38;
    if ($[100] !== th || $[101] !== yMax) {
      t38 = axisOpts(th, "%", yMax);
      $[100] = th;
      $[101] = yMax;
      $[102] = t38;
    } else {
      t38 = $[102];
    }
    const t39 = <div className="h-44"><Bar data={{
        labels: PLAYERS.map(t36),
        datasets: [{
          data: playerData,
          backgroundColor: PLAYERS.map(t37),
          borderRadius: 4
        }]
      }} options={t38} /></div>;
    if ($[103] !== t35 || $[104] !== t39) {
      t11 = <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3">{t35}{t39}</div>;
      $[103] = t35;
      $[104] = t39;
      $[105] = t11;
    } else {
      t11 = $[105];
    }
    t12 = versionOrder.length > 1 && <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3"><div className="text-xs text-[var(--text-muted)] mb-2">{t("Fatal rate by game version")}</div><div className="h-44"><Bar data={{
          labels: versionOrder,
          datasets: [{
            data: versionData,
            backgroundColor: versionOrder.map(v_1 => v_1 === version ? th.gold : th.goldDim),
            borderRadius: 4
          }]
        }} options={axisOpts(th, "%", yMax)} /></div></div>;
    t13 = chars.length > 0 && chars.every(_temp10) && <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3"><div className="text-xs text-[var(--text-muted)] mb-2">{t("Fatal rate by character")}</div><div className="text-sm text-[var(--text-secondary)]">{t("No deaths recorded in this selection")} ({chars.reduce(_temp11, 0).toLocaleString()}{" "}{t("runs")}).</div></div>;
    t14 = chars.length > 0 && chars.some(_temp12) && <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3"><div className="text-xs text-[var(--text-muted)] mb-2">{t("Fatal rate by character")}</div><div className="h-44"><Bar data={{
          labels: charLabels,
          datasets: [{
            data: chars.map(_temp13),
            backgroundColor: chars.map(c_4 => charColor(c_4.character, th.gold)),
            borderRadius: 4
          }]
        }} options={{
          ...axisOpts(th, "%"),
          indexAxis: "y" as const,
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                color: th.text,
                font: {
                  size: 11
                },
                callback: _temp14
              },
              grid: {
                color: th.grid
              }
            },
            y: {
              ticks: {
                color: th.text,
                font: {
                  size: 11
                }
              },
              grid: {
                display: false
              }
            }
          },
          plugins: {
            ...axisOpts(th, "%").plugins,
            tooltip: {
              ...axisOpts(th, "%").plugins.tooltip,
              callbacks: {
                label: c_5 => {
                  const ch = chars[c_5.dataIndex];
                  return ` ${t("{pct}% fatal \xB7 {runs} runs \xB7 {dmg} dmg", {
                    pct: fatalPct(ch) ?? 0,
                    runs: ch.total.toLocaleString(),
                    dmg: ch.avg_damage.toFixed(1)
                  })}`;
                }
              }
            }
          }
        }} /></div></div>;
    $[24] = bracket;
    $[25] = enc;
    $[26] = series.brackets;
    $[27] = series.version_order;
    $[28] = series.versions;
    $[29] = t;
    $[30] = th;
    $[31] = version;
    $[32] = withData;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t9;
  } else {
    t10 = $[33];
    t11 = $[34];
    t12 = $[35];
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
    t16 = $[39];
    t17 = $[40];
    t18 = $[41];
    t9 = $[42];
  }
  let t19;
  if ($[106] !== t10 || $[107] !== t11 || $[108] !== t12 || $[109] !== t13 || $[110] !== t14 || $[111] !== t9) {
    t19 = <div className={t9}>{t10}{t11}{t12}{t13}{t14}</div>;
    $[106] = t10;
    $[107] = t11;
    $[108] = t12;
    $[109] = t13;
    $[110] = t14;
    $[111] = t9;
    $[112] = t19;
  } else {
    t19 = $[112];
  }
  let t20;
  if ($[113] !== t15 || $[114] !== t16 || $[115] !== t17 || $[116] !== t18 || $[117] !== t19) {
    t20 = <div className={t15}>{t16}{t17}{t18}{t19}</div>;
    $[113] = t15;
    $[114] = t16;
    $[115] = t17;
    $[116] = t18;
    $[117] = t19;
    $[118] = t20;
  } else {
    t20 = $[118];
  }
  return t20;
}
function _temp14(v_2) {
  return `${v_2}%`;
}
function _temp13(c_3) {
  return fatalPct(c_3);
}
function _temp12(c_6) {
  return c_6.fatal > 0;
}
function _temp11(n, c_1) {
  return n + c_1.total;
}
function _temp10(c_2) {
  return c_2.fatal === 0;
}
function _temp9(v_0) {
  return <option key={v_0} value={v_0}>{v_0}</option>;
}
function _temp8(on) {
  return `px-2.5 py-1 rounded-md text-xs border transition-colors ${on ? "border-[var(--accent-gold)] text-[var(--accent-gold)] bg-[var(--accent-gold)]/10" : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--accent-gold)]/50"}`;
}
function _temp7(label, value) {
  return <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{label}</div><div className="text-lg font-semibold tabular-nums text-[var(--text-primary)]">{value}</div></div>;
}
function _temp6(x) {
  return x !== null;
}
function _temp5(c_0) {
  return c_0.character.charAt(0) + c_0.character.slice(1).toLowerCase();
}
function _temp4(c) {
  return c.total >= 20;
}
function _temp3(e_0) {
  return [e_0.encounter_id, e_0.encounter_name];
}
function _temp2(a, b) {
  return !a || b.total > a.total ? b : a;
}
function _temp(e) {
  return e.encounter_id;
}